import React from 'react';
import * as Icons from 'lucide-react';

/**
 * Dynamic Lucide Icon renderer with fallback
 */
export function DynamicIcon({ name, className = 'w-5 h-5', ...props }) {
  const IconComponent = Icons[name] || Icons.Scale;
  return React.createElement(IconComponent, { className, ...props });
}

/**
 * Auto capitalize first letter of each word (Ali Valiyev, Jizzax Shahar)
 */
export function capitalizeWords(str) {
  if (!str) return '';
  return str
    .toLowerCase()
    .split(' ')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

/**
 * Format full name to abbreviated professional badge:
 * e.g. "Abdulla Axmedov" -> "A. Axmedov"
 * e.g. "Abdulla Axmedov (boshqaruvchi Rais)" -> "A. Axmedov"
 */
export function formatShortName(fullName) {
  if (!fullName || typeof fullName !== 'string') return '';
  // Remove parenthetical remarks like (boshqaruvchi Rais)
  const cleaned = fullName.replace(/\([^)]*\)/g, '').trim();
  const words = cleaned.split(/\s+/).filter(Boolean);
  if (words.length >= 2) {
    const firstInitial = words[0].charAt(0).toUpperCase();
    const secondWord = words[1].charAt(0).toUpperCase() + words[1].slice(1).toLowerCase();
    return `${firstInitial}. ${secondWord}`;
  }
  if (words.length === 1) {
    return words[0].charAt(0).toUpperCase() + words[0].slice(1);
  }
  return fullName.trim();
}

/**
 * Format phone string to strict professional format: +998 (97) 325-07-70
 * Initial display/mask: +998 ()
 * Gracefully handles backspace without getting stuck on delimiters like ')', '-', or spaces.
 */
export function formatUzPhone(value, prevValue = '', allowEmpty = false) {
  if (!value || typeof value !== 'string') return allowEmpty ? '' : '+998 ()';

  const rawClean = value.trim();
  if (
    rawClean === '' ||
    rawClean === '+' ||
    rawClean === '+9' ||
    rawClean === '+99' ||
    rawClean === '+998' ||
    rawClean === '+998 ' ||
    rawClean === '+998(' ||
    rawClean === '+998 (' ||
    rawClean === '+998 ()'
  ) {
    return allowEmpty ? '' : '+998 ()';
  }

  // Extract digits from current value
  let digits = value.replace(/\D/g, '');
  if (digits.startsWith('998')) {
    digits = digits.slice(3);
  }

  // Extract digits from previous value
  let prevDigits = '';
  if (prevValue && typeof prevValue === 'string') {
    prevDigits = prevValue.replace(/\D/g, '');
    if (prevDigits.startsWith('998')) {
      prevDigits = prevDigits.slice(3);
    }
  }

  // BACKSPACE INTELLIGENCE:
  // If user deleted characters (length shrank) but digit count didn't decrease
  // (because user backspaced a non-digit delimiter like ')', ' ', '-'),
  // we must drop the trailing digit so backspace feels natural and doesn't get stuck!
  if (prevValue && value.length < prevValue.length) {
    if (digits.length === prevDigits.length && digits.length > 0) {
      digits = digits.slice(0, -1);
    }
  } else if (!prevValue) {
    if (value.endsWith(')') || value.endsWith('(') || value.endsWith('-')) {
      digits = digits.slice(0, -1);
    }
  }

  if (!digits || digits.length === 0) {
    return allowEmpty ? '' : '+998 ()';
  }

  digits = digits.slice(0, 9);

  // Exact requested format: +998 (97) 325-07-70
  if (digits.length === 1) {
    return `+998 (${digits}`;
  }
  if (digits.length === 2) {
    return `+998 (${digits}) `;
  }
  if (digits.length <= 5) {
    return `+998 (${digits.slice(0, 2)}) ${digits.slice(2)}`;
  }
  if (digits.length <= 7) {
    return `+998 (${digits.slice(0, 2)}) ${digits.slice(2, 5)}-${digits.slice(5)}`;
  }
  return `+998 (${digits.slice(0, 2)}) ${digits.slice(2, 5)}-${digits.slice(5, 7)}-${digits.slice(7, 9)}`;
}

/**
 * Clean phone to pure E.164-like string for backend lookup or storage
 */
export function cleanUzPhone(formattedPhone) {
  if (!formattedPhone || typeof formattedPhone !== 'string') return '';
  const digits = formattedPhone.replace(/\D/g, '');
  if (digits === '998' || digits.length <= 3) return '';
  if (digits.startsWith('998')) return `+${digits}`;
  return `+998${digits}`;
}

/**
 * Check if entered Uzbekistan phone number has all required digits
 */
export function isCompleteUzPhone(formattedPhone) {
  if (!formattedPhone || typeof formattedPhone !== 'string') return false;
  const digits = formattedPhone.replace(/\D/g, '');
  return digits.length === 12 && digits.startsWith('998');
}

/**
 * Status badge styling
 */
export function getStatusBadge(status) {
  let res;
  switch (status) {
    case 'CONFIRMED':
      res = {
        label: 'Tasdiqlangan',
        classes: 'bg-emerald-50 text-emerald-800 border-emerald-200',
        dot: 'bg-emerald-600',
      };
      break;
    case 'PENDING':
      res = {
        label: 'Kutilmoqda',
        classes: 'bg-amber-50 text-amber-800 border-amber-200',
        dot: 'bg-amber-600',
      };
      break;
    case 'REJECTED':
      res = {
        label: 'Rad etilgan',
        classes: 'bg-rose-50 text-rose-800 border-rose-200',
        dot: 'bg-rose-600',
      };
      break;
    case 'CANCELLED':
      res = {
        label: 'Bekor qilingan',
        classes: 'bg-slate-100 text-slate-700 border-slate-200',
        dot: 'bg-slate-500',
      };
      break;
    case 'COMPLETED':
      res = {
        label: 'Bajarildi',
        classes: 'bg-blue-50 text-blue-800 border-blue-200',
        dot: 'bg-blue-600',
      };
      break;
    case 'NO_SHOW':
      res = {
        label: 'Kelmadi',
        classes: 'bg-purple-50 text-purple-800 border-purple-200',
        dot: 'bg-purple-600',
      };
      break;
    default:
      res = {
        label: status || 'Noma’lum',
        classes: 'bg-slate-100 text-slate-600 border-slate-200',
        dot: 'bg-slate-400',
      };
      break;
  }
  res.className = res.classes;
  return res;
}

/**
 * Format date string (YYYY-MM-DD) to friendly format
 */
export function formatDate(dateStr, locale = 'uz') {
  if (!dateStr) return '';
  try {
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return dateStr;
    const options = { year: 'numeric', month: 'long', day: 'numeric' };
    const loc = locale === 'uz' ? 'uz-UZ' : locale === 'ru' ? 'ru-RU' : 'en-US';
    return d.toLocaleDateString(loc, options);
  } catch {
    return dateStr;
  }
}

/**
 * Full Uzbekistan Regions and Districts / Cities Directory
 */
export const UZBEKISTAN_REGIONS = {
  'Jizzax viloyati': [
    'Jizzax shahar',
    'Sharof Rashidov tumani',
    'Zomin tumani',
    'Baxmal tumani',
    'G‘allaorol tumani',
    'Do‘stlik tumani',
    'Zarbdor tumani',
    'Zafarobod tumani',
    'Mirzacho‘l tumani',
    'Paxtakor tumani',
    'Forish tumani',
    'Yangiobod tumani',
    'Arnasoy tumani'
  ],
  'Toshkent shahri': [
    'Yunusobod tumani',
    'Mirzo Ulug‘bek tumani',
    'Chilonzor tumani',
    'Yakkasaroy tumani',
    'Mirobod tumani',
    'Shayxontohur tumani',
    'Olmazor tumani',
    'Uchtepa tumani',
    'Sergeli tumani',
    'Yangi Hayot tumani',
    'Bektemir tumani',
    'Yashnobod tumani'
  ],
  'Toshkent viloyati': [
    'Nurafshon shahar',
    'Chirchiq shahar',
    'Olmaliq shahar',
    'Angren shahar',
    'Bekobod shahar',
    'Bo‘stonliq tumani',
    'Qibray tumani',
    'Zangiota tumani',
    'Parkent tumani',
    'Yuqori Chirchiq tumani',
    'O‘rta Chirchiq tumani',
    'Quyi Chirchiq tumani',
    'Chinoz tumani',
    'Yangiyo‘l tumani'
  ],
  'Samarqand viloyati': [
    'Samarqand shahar',
    'Kattaqo‘rg‘on shahar',
    'Samarqand tumani',
    'Pastdarg‘om tumani',
    'Urgut tumani',
    'Bulung‘ur tumani',
    'Jomboy tumani',
    'Ishtixon tumani',
    'Narpay tumani',
    'Payariq tumani',
    'Toyloq tumani'
  ],
  'Farg‘ona viloyati': [
    'Farg‘ona shahar',
    'Marg‘ilon shahar',
    'Qo‘qon shahar',
    'Quvasoy shahar',
    'Beshariq tumani',
    'Bog‘dod tumani',
    'Buvayda tumani',
    'Dang‘ara tumani',
    'Quva tumani',
    'Rishton tumani',
    'Oltiariq tumani',
    'Uchko‘prik tumani'
  ],
  'Andijon viloyati': [
    'Andijon shahar',
    'Xonobod shahar',
    'Andijon tumani',
    'Asaka tumani',
    'Baliqchi tumani',
    'Bo‘z tumani',
    'Buloqboshi tumani',
    'Izboskan tumani',
    'Jalaquduq tumani',
    'Marhamat tumani',
    'Oltinko‘l tumani',
    'Paxtaobod tumani',
    'Shahrixon tumani',
    'Xo‘jaobod tumani'
  ],
  'Namangan viloyati': [
    'Namangan shahar',
    'Chortoq tumani',
    'Chust tumani',
    'Kosonsoy tumani',
    'Mingbuloq tumani',
    'Namangan tumani',
    'Norin tumani',
    'Pop tumani',
    'To‘raqo‘rg‘on tumani',
    'Uychi tumani',
    'Uchqo‘rg‘on tumani',
    'Yangiqo‘rg‘on tumani'
  ],
  'Buxoro viloyati': [
    'Buxoro shahar',
    'Kogon shahar',
    'Buxoro tumani',
    'G‘ijduvon tumani',
    'Jondor tumani',
    'Kogon tumani',
    'Olot tumani',
    'Peshku tumani',
    'Romitan tumani',
    'Shofirkon tumani',
    'Vobkent tumani'
  ],
  'Navoiy viloyati': [
    'Navoiy shahar',
    'Zarafshon shahar',
    'Karmana tumani',
    'Qiziltepa tumani',
    'Konimex tumani',
    'Navbahor tumani',
    'Nurota tumani',
    'Tomdi tumani',
    'Uchquduq tumani',
    'Xatirchi tumani'
  ],
  'Qashqadaryo viloyati': [
    'Qarshi shahar',
    'Shahrisabz shahar',
    'Dehqonobod tumani',
    'Kasbi tumani',
    'Kitob tumani',
    'Koson tumani',
    'Ko‘kdala tumani',
    'Mirishkor tumani',
    'Muborak tumani',
    'Nishon tumani',
    'Qamashi tumani',
    'Qarshi tumani',
    'Yakkabog‘ tumani',
    'G‘uzor tumani'
  ],
  'Surxondaryo viloyati': [
    'Termiz shahar',
    'Angor tumani',
    'Bandixon tumani',
    'Boysun tumani',
    'Denov tumani',
    'Jarqo‘rg‘on tumani',
    'Qiziriq tumani',
    'Qumqo‘rg‘on tumani',
    'Muzrabot tumani',
    'Oltinsoy tumani',
    'Sariosiyo tumani',
    'Termiz tumani',
    'Uzun tumani',
    'Sherobod tumani',
    'Sho‘rchi tumani'
  ],
  'Xorazm viloyati': [
    'Urganch shahar',
    'Xiva shahar',
    'Bog‘ot tumani',
    'Gurlan tumani',
    'Qo‘shko‘pir tumani',
    'Shovot tumani',
    'Urganch tumani',
    'Xiva tumani',
    'Xonqa tumani',
    'Hazorasp tumani',
    'Yangiariq tumani',
    'Yangibozor tumani',
    'Tuproqqal’a tumani'
  ],
  'Sirdaryo viloyati': [
    'Guliston shahar',
    'Shirin shahar',
    'Yangiyer shahar',
    'Boyovut tumani',
    'Guliston tumani',
    'Mirzaobod tumani',
    'Oqoltin tumani',
    'Sardoba tumani',
    'Sayxunobod tumani',
    'Sirdaryo tumani',
    'Xovos tumani'
  ],
  'Qoraqalpog‘iston Respublikasi': [
    'Nukus shahar',
    'Amudaryo tumani',
    'Beruniy tumani',
    'Bo‘zatov tumani',
    'Chimboy tumani',
    'Ellikqala tumani',
    'Kegeyli tumani',
    'Mo‘ynoq tumani',
    'Nukus tumani',
    'Qanliko‘l tumani',
    'Qo‘ng‘irot tumani',
    'Qorao‘zak tumani',
    'Shumanay tumani',
    'Taxtako‘pir tumani',
    'To‘rtko‘l tumani',
    'Xo‘jayli tumani',
    'Taxiatosh tumani'
  ]
};

/**
 * Universal Day Off & Slot Reason Localizer across 3 languages (Uz, Ru, En)
 */
export function localizeDayOffReason(reason = '', lang = 'uz') {
  if (!reason) {
    if (lang === 'en') return 'Day off';
    if (lang === 'ru') return 'Выходной день';
    return 'Dam olish kuni';
  }

  let text = reason
    .replace(/\s*\([^\)]*qabul[^\)]*\)/gi, '')
    .replace(/\s*—\s*qabul\s*yo[‘'`]?q/gi, '')
    .trim();

  if (lang === 'en') {
    return text
      .replace(/Dushanba/gi, 'Monday')
      .replace(/Seshanba/gi, 'Tuesday')
      .replace(/Chorshanba/gi, 'Wednesday')
      .replace(/Payshanba/gi, 'Thursday')
      .replace(/Juma/gi, 'Friday')
      .replace(/Shanba/gi, 'Saturday')
      .replace(/Yakshanba/gi, 'Sunday')
      .replace(/—\s*dam\s*olish\s*kuni/gi, '— Day off')
      .replace(/dam\s*olish\s*kuni:?/gi, 'Day off:')
      .replace(/dam\s*olish\s*kuni/gi, 'Day off')
      .replace(/Bu sana o[‘'`]?tib ketgan, iltimos boshqa sanani tanlang/gi, 'This date has passed, please select another date')
      .replace(/Bugungi kundagi barcha qabul vaqtlari o[‘'`]?tib ketgan yoki band/gi, 'All appointment slots for today have passed or are booked')
      .replace(/Ushbu kunda barcha qabul vaqtlari band/gi, 'All appointment slots on this date are fully booked')
      .replace(/Ushbu kunda qabul vaqtlari mavjud emas/gi, 'No appointment slots available on this date')
      .replace(/Qabul vaqtlari yuklanmadi/gi, 'Appointment slots could not be loaded');
  }

  if (lang === 'ru') {
    return text
      .replace(/Dushanba/gi, 'Понедельник')
      .replace(/Seshanba/gi, 'Вторник')
      .replace(/Chorshanba/gi, 'Среда')
      .replace(/Payshanba/gi, 'Четверг')
      .replace(/Juma/gi, 'Пятница')
      .replace(/Shanba/gi, 'Суббота')
      .replace(/Yakshanba/gi, 'Воскресенье')
      .replace(/—\s*dam\s*olish\s*kuni/gi, '— выходной день')
      .replace(/dam\s*olish\s*kuni:?/gi, 'Выходной день:')
      .replace(/dam\s*olish\s*kuni/gi, 'Выходной день')
      .replace(/Bu sana o[‘'`]?tib ketgan, iltimos boshqa sanani tanlang/gi, 'Эта дата уже прошла, пожалуйста, выберите другую дату')
      .replace(/Bugungi kundagi barcha qabul vaqtlari o[‘'`]?tib ketgan yoki band/gi, 'Все часы приёма на сегодня уже прошли или заняты')
      .replace(/Ushbu kunda barcha qabul vaqtlari band/gi, 'Все часы приёма на эту дату заняты')
      .replace(/Ushbu kunda qabul vaqtlari mavjud emas/gi, 'На эту дату часы приёма отсутствуют')
      .replace(/Qabul vaqtlari yuklanmadi/gi, 'Не удалось загрузить часы приёма');
  }

  return text;
}

/**
 * Localize lawyer position badge (Advokat -> Attorney / Адвокат)
 */
export function localizeLawyerPosition(position = 'Advokat', lang = 'uz') {
  if (lang === 'en') return 'Attorney';
  if (lang === 'ru') return 'Адвокат';
  return 'Advokat';
}

/**
 * Localize lawyer specialization across 3 languages
 */
export function localizeSpecialization(spec = '', lang = 'uz') {
  if (!spec) return '';
  if (lang === 'uz') return spec;

  const map = {
    ru: {
      'Jinoyat va Iqtisodiy Sud Himoyasi': 'Защита по уголовным и экономическим делам',
      'Korporativ Nizolar va Biznes Himoyasi': 'Корпоративные споры и защита бизнеса',
      'Fuqarolik va Mehnat Huquqi Himoyasi': 'Защита по гражданскому и трудовому праву',
      'Oila, Meros va Mulkiy Nizolar Himoyasi': 'Семейные, наследственные и имущественные споры',
      'Soliq va Bojxona Nizolari Himoyasi': 'Защита по налоговым и таможенным спорам',
      'Iqtisodiy va Shartnomaviy Nizolar': 'Экономические и договорные споры',
      'Sud va Tergov Himoyasi': 'Судебная и следственная защита',
      'Biznes va Shartnomalar': 'Бизнес и контракты',
    },
    en: {
      'Jinoyat va Iqtisodiy Sud Himoyasi': 'Criminal & Economic Court Defense',
      'Korporativ Nizolar va Biznes Himoyasi': 'Corporate Disputes & Business Defense',
      'Fuqarolik va Mehnat Huquqi Himoyasi': 'Civil & Labor Law Defense',
      'Oila, Meros va Mulkiy Nizolar Himoyasi': 'Family, Inheritance & Property Disputes',
      'Soliq va Bojxona Nizolari Himoyasi': 'Tax & Customs Disputes Defense',
      'Iqtisodiy va Shartnomaviy Nizolar': 'Economic & Contractual Disputes',
      'Sud va Tergov Himoyasi': 'Court & Investigation Defense',
      'Biznes va Shartnomalar': 'Business & Contracts',
    },
  };

  if (map[lang]?.[spec]) return map[lang][spec];

  // Pattern replacement fallback
  let res = spec;
  if (lang === 'en') {
    res = res
      .replace(/Jinoyat\s+va\s+Iqtisodiy\s+Sud\s+Himoyasi/gi, 'Criminal & Economic Court Defense')
      .replace(/Jinoyat\s+ishlari/gi, 'Criminal Cases')
      .replace(/Iqtisodiy/gi, 'Economic')
      .replace(/Fuqarolik/gi, 'Civil')
      .replace(/Korporativ/gi, 'Corporate')
      .replace(/Biznes/gi, 'Business')
      .replace(/Oila\s+va\s+meros/gi, 'Family & Inheritance')
      .replace(/Sud\s+Himoyasi/gi, 'Court Defense')
      .replace(/Himoyasi/gi, 'Defense');
  } else if (lang === 'ru') {
    res = res
      .replace(/Jinoyat\s+va\s+Iqtisodiy\s+Sud\s+Himoyasi/gi, 'Защита по уголовным и экономическим делам')
      .replace(/Jinoyat\s+ishlari/gi, 'Уголовные дела')
      .replace(/Iqtisodiy/gi, 'Экономические')
      .replace(/Fuqarolik/gi, 'Гражданские')
      .replace(/Korporativ/gi, 'Корпоративные')
      .replace(/Biznes/gi, 'Бизнес')
      .replace(/Oila\s+va\s+meros/gi, 'Семейные и наследственные')
      .replace(/Sud\s+Himoyasi/gi, 'Судебная защита')
      .replace(/Himoyasi/gi, 'Защита');
  }
  return res;
}

/**
 * Localize lawyer consultation languages (O'zbek, Rus, Ingliz)
 */
export function localizeLanguages(languages = '', lang = 'uz') {
  if (!languages) {
    if (lang === 'en') return 'Uzbek, Russian, English';
    if (lang === 'ru') return 'Узбекский, Русский, Английский';
    return 'O‘zbek, Rus, Ingliz';
  }
  if (lang === 'en') {
    return languages
      .replace(/O[‘'`]?zbek/gi, 'Uzbek')
      .replace(/Rus/gi, 'Russian')
      .replace(/Ingliz/gi, 'English');
  }
  if (lang === 'ru') {
    return languages
      .replace(/O[‘'`]?zbek/gi, 'Узбекский')
      .replace(/Rus/gi, 'Русский')
      .replace(/Ingliz/gi, 'Английский');
  }
  return languages;
}

/**
 * Localize office name (Jizzax (Markaziy Bosh Ofis))
 */
export function localizeOfficeName(name = '', lang = 'uz') {
  if (!name) return '';
  if (lang === 'en') {
    return name
      .replace(/Markaziy\s+Bosh\s+Ofis/gi, 'Central Head Office')
      .replace(/Bosh\s+Ofis/gi, 'Head Office')
      .replace(/Jizzax/gi, 'Jizzakh');
  }
  if (lang === 'ru') {
    return name
      .replace(/Markaziy\s+Bosh\s+Ofis/gi, 'Центральный Главный Офис')
      .replace(/Bosh\s+Ofis/gi, 'Головной Офис')
      .replace(/Jizzax/gi, 'Джизак');
  }
  return name;
}

/**
 * Localize working hours text (Dush – Shanba: 09:00 – 18:00)
 */
export function localizeWorkingHours(text = '', lang = 'uz') {
  if (!text) {
    if (lang === 'en') return 'Mon – Sat: 09:00 – 18:00 (Sunday: Day off)';
    if (lang === 'ru') return 'Пн – Сб: 09:00 – 18:00 (Воскресенье: Выходной)';
    return 'Dush – Shanba: 09:00 – 18:00 (Yakshanba: Dam olish)';
  }
  if (lang === 'en') {
    return text
      .replace(/Dushanba/gi, 'Monday')
      .replace(/Dush\b/gi, 'Mon')
      .replace(/Seshanba/gi, 'Tuesday')
      .replace(/Sesh\b/gi, 'Tue')
      .replace(/Chorshanba/gi, 'Wednesday')
      .replace(/Chor\b/gi, 'Wed')
      .replace(/Payshanba/gi, 'Thursday')
      .replace(/Pay\b/gi, 'Thu')
      .replace(/Juma/gi, 'Friday')
      .replace(/Jum\b/gi, 'Fri')
      .replace(/Shanba/gi, 'Saturday')
      .replace(/Shan\b/gi, 'Sat')
      .replace(/Yakshanba/gi, 'Sunday')
      .replace(/Yak\b/gi, 'Sun')
      .replace(/Dam\s*olish\s*kuni?/gi, 'Day off')
      .replace(/Dam\s*olish/gi, 'Day off')
      .replace(/Yopiq/gi, 'Closed');
  }
  if (lang === 'ru') {
    return text
      .replace(/Dushanba/gi, 'Понедельник')
      .replace(/Dush\b/gi, 'Пн')
      .replace(/Seshanba/gi, 'Вторник')
      .replace(/Sesh\b/gi, 'Вт')
      .replace(/Chorshanba/gi, 'Среда')
      .replace(/Chor\b/gi, 'Ср')
      .replace(/Payshanba/gi, 'Четверг')
      .replace(/Pay\b/gi, 'Чт')
      .replace(/Juma/gi, 'Пятница')
      .replace(/Jum\b/gi, 'Пт')
      .replace(/Shanba/gi, 'Суббота')
      .replace(/Shan\b/gi, 'Сб')
      .replace(/Yakshanba/gi, 'Воскресенье')
      .replace(/Yak\b/gi, 'Вс')
      .replace(/Dam\s*olish\s*kuni?/gi, 'Выходной')
      .replace(/Dam\s*olish/gi, 'Выходной')
      .replace(/Yopiq/gi, 'Закрыто');
  }
  return text;
}
